import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useMemo } from 'react';
import { useCognitoAuthStatus } from './useCognitoAuthStatus';

export const useConvexCognitoAuth = () => {
  const authStatus = useCognitoAuthStatus();
  const isAuthenticated = authStatus === 'authenticated';

  /**
   * Convex calls this function automatically with `forceRefreshToken` set to `true`.
   * https://docs.convex.dev/auth/advanced/custom-auth
   */
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      try {
        /**
         * DO NOT use `forceRefreshToken`.
         * Amplify SDK automatically handles refreshing idToken when it has expired.
         *
         * Even though Convex docs recommend to force the refresh, do not do it.
         * Calling `await fetchAuthSession();` fetches from local storage it seems (no network call is made).
         *
         * `await fetchAuthSession({ forceRefresh: forceRefreshToken });` does an API call to Cognito service.
         * If this call is made when the POS is OFFLINE, and the idToken has expired then Cognito we change to "unauthenticated" state.
         */

        /**
         * If the token is still valid then it will return the cached token (it seems from local storage)
         * Otherwise, the token will be refreshed which takes a 1-2 seconds.
         */
        const { tokens } = await fetchAuthSession();

        const idToken = tokens?.idToken;
        if (!idToken) return null;

        if (idToken.payload.exp) {
          console.log('idToken expires at:', new Date(idToken.payload.exp * 1000));
        }
        return idToken.toString();
      } catch (error) {
        console.error('[auth] Error fetching id token:', error);
        return null;
      }
    },
    [],
  );

  return useMemo(
    () => ({
      // The async function to fetch the ID token
      fetchAccessToken,
      /**
       * When `isAuthenticated` is `false`, Convex we stop trying to reconnect the Websocket connection.
       */
      isAuthenticated: isAuthenticated,
      // Whether the auth provider is in a loading state
      isLoading: authStatus === 'configuring',
    }),
    [fetchAccessToken, isAuthenticated, authStatus],
  );
};
